// src/hooks/useAuth.js
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

// === Auth real (usa el mismo storage que authService: key "auth") ===
import {
  readAuthSession,
  saveAuthSession,
  clearAuthSession as hardClearAuth,
  isTokenExpired as isExpiredByAuthService, // valida contra expires_at (segundos)
} from "../services/authService";

// ---------------- THEME (igual que tenías) ----------------
const bgColor = "bg-[#ebebeb]";
const bgSearchColor = "bg-[#d7d7d7]";
const hoveInputsColor = "hover:bg-[#d0d0d0]";
const focusInputsColor = "focus:bg-[#d0d0d0]";
const lettersColor = "text-[#4c4c4c]";
const lettersPlaceholderColor = "placeholder-[#848484]";
const lettersTitlesColor = "text-[#012566]";
const buttonColor = "bg-[#012566]";
const buttonHoverColor = "hover:bg-[#324a74]";
const linesColor = "border-[#c7c7c7]";
const hoverListOption = "hover:bg-[#e1e0e0]";
const buttonText = "text-[#ebebeb]";
const buttonNavIconColor = "text-[#012566]";
const buttonNavIconShadow = "shadow-[#acacac]";

// ---------------- Contexto ----------------
const AuthContext = createContext(null);

// Utilitario para compatibilidad con el login “antiguo”
function buildSessionFromLegacy(token, user, expiresAtSeconds) {
  if (!token || !user) return null;
  const expires_at =
    typeof expiresAtSeconds === "number"
      ? expiresAtSeconds
      : Math.floor(Date.now() / 1000) + 3600; // fallback 1h
  return {
    access_token: token,
    refresh_token: null,
    token_type: "bearer",
    user,
    expires_at,
  };
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const mountedRef = useRef(false);

  // ---- Limpia sesión y redirige al login
  const logout = useCallback(
    (options = { reason: "manual" }) => {
      const reason =
        typeof options === "string" ? options : options?.reason || "manual";

      console.debug("[auth] logout called, reason:", reason, "currentPath:", location.pathname);

      hardClearAuth(); // borra "auth" en localStorage
      setIsAuthenticated(false);
      setUser(null);

      if (reason === "expired" && !location.pathname.startsWith("/reset-password")) {
        if (!location.pathname.startsWith("/reset-password")) {
          toast.error("Sesión expirada. Inicia sesión nuevamente.");
        }
      }

      if (!location.pathname.startsWith("/reset-password") && location.pathname !== "/") {
        navigate("/", { replace: true });
      }
    },
    [navigate, location.pathname]
  );

  // ---- Lee del storage “auth” y actualiza estado
  const restoreFromStorage = useCallback(() => {
    const s = readAuthSession(); // { access_token, user, expires_at, ... } | null
    if (!s?.access_token || !s?.user) {
      setIsAuthenticated(false);
      setUser(null);
      return;
    }
    if (isExpiredByAuthService(s.expires_at)) {
      logout({ reason: "expired" });
      return;
    }
    setIsAuthenticated(true);
    setUser(s.user);
  }, [logout]);

  // ---- Login flexible:
  //  A) login(sessionLike) → { access_token, user, expires_at? } (nuevo flujo)
  //  B) login(token, user, expires_at?) (flujo anterior)
  const login = useCallback(
    (sessionOrToken, maybeUser, maybeExpiresAtSec) => {
      // Caso A: objeto de sesión
      if (
        sessionOrToken &&
        typeof sessionOrToken === "object" &&
        sessionOrToken.access_token &&
        sessionOrToken.user
      ) {
        const s = {
          access_token: sessionOrToken.access_token,
          refresh_token: sessionOrToken.refresh_token ?? null,
          token_type: sessionOrToken.token_type ?? "bearer",
          user: sessionOrToken.user,
          expires_at:
            sessionOrToken.expires_at ??
            Math.floor(Date.now() / 1000) + (sessionOrToken.expires_in || 3600),
        };
        if (isExpiredByAuthService(s.expires_at)) {
          logout({ reason: "expired" });
          return false;
        }
        saveAuthSession(s);
        setIsAuthenticated(true);
        setUser(s.user);
        return true;
      }

      // Caso B: compat con login(token, user, expires_at?)
      const legacy = buildSessionFromLegacy(
        sessionOrToken,
        maybeUser,
        maybeExpiresAtSec
      );
      if (!legacy) return false;
      if (isExpiredByAuthService(legacy.expires_at)) {
        logout({ reason: "expired" });
        return false;
      }
      saveAuthSession(legacy);
      setIsAuthenticated(true);
      setUser(legacy.user);
      return true;
    },
    [logout]
  );

  // ---- Montaje inicial
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    setLoading(true);
    try {
      restoreFromStorage();
    } finally {
      setLoading(false);
    }

    // Escucha cambios de sesión entre pestañas
    const onStorage = (e) => {
      if (e.key === "auth") {
        restoreFromStorage();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [restoreFromStorage]);

  // ---- Vigila navegación: si no hay sesión válida, fuerza salir
  useEffect(() => {
    const s = readAuthSession();
    if (!s?.access_token || !s?.user || isExpiredByAuthService(s?.expires_at)) {
      if (location.pathname !== "/" && !location.pathname.startsWith("/reset-password")) {
        logout({ reason: "expired" });
      }
    }
  }, [location.pathname, logout]);  

  const value = {
    // estado auth
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    refreshFromStorage: restoreFromStorage,

    // tema
    bgColor,
    lettersColor,
    lettersTitlesColor,
    buttonColor,
    buttonHoverColor,
    linesColor,
    bgSearchColor,
    lettersPlaceholderColor,
    hoverListOption,
    buttonText,
    buttonNavIconColor,
    buttonNavIconShadow,
    hoveInputsColor,
    focusInputsColor,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  }
  return ctx;
}
